import { useMemo, useState } from 'react';
import { formatDateTime } from '../lib/datetime';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { Sparkline } from '../components/Charts';
import { NoteDialog, CoachDialog } from '../components/triage/TriageModals';
import { kpisService } from '../lib/services';
import { useApi } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { useCustomerOptions, useConsultantOptions } from '../lib/useDirectory';
import { apiErrorMessage } from '../lib/apiError';
import {
  TRIAGE_TABS, TRIAGE_WINDOWS, BULK_ACTIONS, queueParams, rangeLabel, pageCount,
  canGoPrev, canGoNext, tabLabel, statusPillClass, flagPillClass,
  toggleId, toggleAll, bulkDoneText,
} from '../lib/triage';

const PAGE_SIZE = 50;

/** Save a Blob as a file in the browser. */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function TriageQueue({ onOpenCall, onOpenCoaching }) {
  const { toast } = useToast();
  const [tabIdx, setTabIdx]     = useState(0);
  const [days, setDays]         = useState(7);
  const [search, setSearch]     = useState('');
  const [q, setQ]               = useState('');
  const [page, setPage]         = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkMenu, setBulkMenu] = useState(false);
  const [dialog, setDialog]     = useState(null);   // { action } while a bulk dialog is open
  const [busy, setBusy]         = useState(false);
  const [exporting, setExporting] = useState(false);

  const tab = TRIAGE_TABS[tabIdx];

  const { data: queue, error, loading, refetch } = useApi(
    () => kpisService.triageQueuePage(queueParams({ tab: tab.id, q, days, page, size: PAGE_SIZE })),
    { fallback: null, deps: [tab.id, q, days, page] },
  );
  const { data: summary, refetch: refetchSummary } = useApi(
    () => kpisService.triageSummary({ days: days || 90 }),
    { fallback: null, deps: [days] },
  );
  const { options: customerOptions } = useCustomerOptions();
  const { options: userOptions, loading: usersLoading } = useConsultantOptions();

  const names = useMemo(() => {
    const m = new Map();
    (customerOptions || []).forEach((o) => m.set(o.id, o.primary));
    return m;
  }, [customerOptions]);

  const rows = useMemo(() => (queue?.content || []).map((c) => ({
    id:       c.callId,
    t:        formatDateTime(c.timestamp, '—'),
    cust:     names.get(c.customerId) || '—',
    cid:      c.customerId || null,
    q:        c.qualityScore ?? 0,
    sent:     c.sentiment ?? 0,
    flags:    c.flags || [],
    status:   c.status || 'Review',
    critical: c.status === 'CRITICAL',
  })), [queue, names]);

  const counts = queue?.counts;
  const total = queue?.totalElements ?? 0;
  const pageIds = rows.map((r) => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const windowLabel = summary?.windowDays ? `${summary.windowDays}D` : '7D';

  const queueKpis = useMemo(() => {
    const calls    = summary?.callsInWindow ?? 0;
    const quality  = summary?.avgQuality ?? 0;
    const flagged  = summary?.flagged ?? 0;
    const critical = summary?.critical ?? 0;
    const coaching = summary?.coachingAssigned ?? 0;
    const flaggedPct = calls > 0 ? ((flagged / calls) * 100).toFixed(1) : '0.0';
    return [
      [`CALLS · ${windowLabel}`, `${calls}`,   `${summary?.callsAllTime ?? 0} all-time`,   'var(--accent-primary)'],
      ['AVG QUALITY',  `${quality}`, '/100 · estimated from sentiment',                    quality < 50 ? 'var(--danger)' : quality < 70 ? 'var(--warning)' : 'var(--success)'],
      ['FLAGGED',      `${flagged}`, `${flaggedPct}% of calls · not yet resolved`,         flagged > 0 ? 'var(--warning)' : 'var(--success)'],
      ['CRITICAL',     `${critical}`,'sentiment < −0.5 · not yet resolved',                critical > 0 ? 'var(--danger)' : 'var(--success)'],
      ['COACHING',     `${coaching}`,'calls with open coaching',                           'var(--accent-primary)'],
    ];
  }, [summary, windowLabel]);

  const histogram = summary?.perDay && summary.perDay.length > 0 ? summary.perDay : [0, 0, 0, 0, 0, 0, 0];

  /* ── navigation / filters ── */
  const clearSelection = () => setSelected(new Set());
  const pickTab = (i) => { setTabIdx(i); setPage(0); clearSelection(); };
  const pickDays = (d) => { setDays(d); setPage(0); clearSelection(); };
  const applySearch = () => { setQ(search.trim()); setPage(0); clearSelection(); };
  const goPage = (p) => { setPage(p); clearSelection(); };
  const reload = () => { refetch(); refetchSummary(); };

  /* ── export ── */
  const onExport = async () => {
    setExporting(true);
    try {
      const p = queueParams({ tab: tab.id, q, days });
      delete p.page;
      delete p.size;
      const blob = await kpisService.triageExport(p);
      downloadBlob(blob, `triage-calls-${tab.id}.csv`);
      toast(`Exported "${tab.label}" calls`, { variant: 'success' });
    } catch (err) {
      toast(`Export failed: ${apiErrorMessage(err, 'unknown error')}`, { variant: 'danger' });
    } finally {
      setExporting(false);
    }
  };

  /* ── bulk review ── */
  const runBulk = async (action, extra = {}) => {
    setBusy(true);
    try {
      const res = await kpisService.bulkReview({ ids: [...selected], action, ...extra });
      toast(bulkDoneText(action, res?.updated ?? 0, res?.requested ?? selected.size), { variant: 'success' });
      setDialog(null);
      setBulkMenu(false);
      clearSelection();
      reload();
    } catch (err) {
      toast(`Bulk review failed: ${apiErrorMessage(err, 'unknown error')}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const onBulkPick = (entry) => {
    setBulkMenu(false);
    if (entry.needsCoach || entry.action === 'RESOLVE' || entry.action === 'ESCALATE') {
      setDialog({ action: entry.action, label: entry.label });
    } else {
      runBulk(entry.action);
    }
  };

  const selectedCount = selected.size;

  return (
    <>
      <TopBar crumbs={['Triage & QA', 'Call queue']} right={
        <>
          <button className="btn small" disabled={exporting} onClick={onExport}>
            {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button className="btn small" onClick={() => onOpenCoaching?.()}>Coaching library</button>
          <div style={{ position: 'relative' }}>
            <button
              className="btn small primary"
              disabled={selectedCount === 0}
              title={selectedCount === 0 ? 'Tick one or more calls first' : undefined}
              onClick={() => setBulkMenu((v) => !v)}
            >
              Bulk review ({selectedCount})
            </button>
            {bulkMenu && selectedCount > 0 && (
              <div
                className="card"
                style={{ position: 'absolute', right: 0, top: '110%', zIndex: 20, minWidth: 260, padding: 6 }}
              >
                {BULK_ACTIONS.map((entry) => (
                  <button
                    key={entry.action}
                    type="button"
                    className="btn small"
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none' }}
                    onClick={() => onBulkPick(entry)}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      } />
      <div className="app-content">
        <div className="grid-5" style={{ gridTemplateColumns: 'repeat(5, 1fr) 1.6fr', marginBottom: 16 }}>
          {queueKpis.map(([l, v, sub, c], i) => (
            <div key={i} className="card animate-in" style={{ animationDelay: `${i * 0.04}s` }}>
              <div className="card-label">{l}</div>
              <div className="metric-value" style={{ fontSize: 28, color: c }}>{v}</div>
              <div className="mono text-xs text-muted">{sub}</div>
            </div>
          ))}
          <div className="card animate-in" style={{ animationDelay: '0.2s' }}>
            <div className="card-label">CALLS PER DAY · 7D</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60, marginTop: 8 }}>
              {(() => {
                const bars = histogram.slice(-7);
                const max = Math.max(1, ...bars);
                return bars.map((v, i) => (
                  <div
                    key={i}
                    title={`${v} calls`}
                    style={{
                      flex: 1,
                      height: `${(v / max) * 100}%`,
                      background: v === 0 ? 'var(--border-color)'
                        : i === bars.length - 1 ? 'var(--accent-primary)' : 'var(--info)',
                      opacity: 0.85,
                      borderRadius: '2px 2px 0 0',
                      minHeight: 2,
                    }}
                  />
                ));
              })()}
            </div>
            <div className="flex-between mono text-xs text-muted" style={{ marginTop: 4 }}>
              <span>7d ago</span>
              <span>today</span>
            </div>
          </div>
        </div>

        <div className="tab-bar" style={{ marginBottom: 12 }}>
          {TRIAGE_TABS.map((t, i) => (
            <div key={t.id} className={`tab-item ${i === tabIdx ? 'active' : ''}`} onClick={() => pickTab(i)}>
              {tabLabel(t, counts)}
            </div>
          ))}
          <div style={{ marginLeft: 'auto', padding: '8px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              className="input input-compact"
              value={search}
              placeholder="Search call ID…"
              style={{ width: 180 }}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
            />
            <button className="btn small btn-compact" onClick={applySearch}>Search</button>
            <select className="input input-compact" value={days} onChange={(e) => pickDays(Number(e.target.value))} aria-label="Time window">
              {TRIAGE_WINDOWS.map((w) => <option key={w.days} value={w.days}>{w.label}</option>)}
            </select>
            <span className="mono text-xs text-muted">{rangeLabel({ page, size: PAGE_SIZE, total, shown: rows.length })}</span>
          </div>
        </div>

        {error && !queue && (
          <div className="card" style={{ marginBottom: 12, padding: 12, color: 'var(--danger)' }}>
            Couldn’t load the call queue: {apiErrorMessage(error, 'unknown error')}{' '}
            <button className="btn small" onClick={reload}>Retry</button>
          </div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all calls on this page"
                      checked={allOnPage}
                      disabled={rows.length === 0}
                      onChange={() => setSelected((s) => toggleAll(s, pageIds))}
                    />
                  </th>
                  {['Call ID', 'Time', 'Customer', 'Quality (est.)', 'Sentiment', 'Flags', 'Status', ''].map((h, i) => <th key={i}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => onOpenCall?.(r.id)}
                    style={{
                      background: r.critical ? 'var(--danger-bg)' : 'transparent',
                      cursor: onOpenCall ? 'pointer' : 'default',
                    }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select call ${String(r.id).slice(0, 8)}`}
                        checked={selected.has(r.id)}
                        onChange={() => setSelected((s) => toggleId(s, r.id))}
                      />
                    </td>
                    <td
                      className="mono text-xs"
                      style={{ color: 'var(--info)', cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); onOpenCall?.(r.id); }}
                    >{r.id}</td>
                    <td className="mono text-xs">{r.t}</td>
                    <td style={{ fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                      {r.cid ? (
                        <Link to={`/customers/${r.cid}`} style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>{r.cust}</Link>
                      ) : r.cust}
                    </td>
                    <td>
                      <div className="flex-row" style={{ gap: 6 }}>
                        <span className="mono" style={{ fontWeight: 700, fontSize: 16, width: 28, color: r.q < 50 ? 'var(--danger)' : r.q < 70 ? 'var(--warning)' : 'var(--success)' }}>{r.q}</span>
                        <div className="progress-bar" style={{ flex: 1 }}>
                          <div className="fill" style={{ width: `${r.q}%`, background: r.q < 50 ? 'var(--danger)' : r.q < 70 ? 'var(--warning)' : 'var(--success)' }} />
                        </div>
                      </div>
                    </td>
                    <td>
                      <Sparkline values={[0, r.sent * 0.3, r.sent * 0.6, r.sent * 0.4, r.sent * 1.2, r.sent].map((v) => v + 1)} w={80} h={22} color={r.sent < -0.3 ? 'var(--danger)' : 'var(--warning)'} />
                      <div className="mono text-xs text-muted">{r.sent.toFixed(2)}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.flags.map((f, fi) => (
                          <span key={fi} className={`pill ${flagPillClass(f)}`} style={{ fontSize: 9 }}>{f}</span>
                        ))}
                      </div>
                    </td>
                    <td><span className={`pill ${statusPillClass(r.status)}`}>{r.status}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="btn small primary" onClick={() => onOpenCall?.(r.id)}>
                        Open →
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && !error && (
                  <tr>
                    <td colSpan={9} style={{ padding: '28px 12px', textAlign: 'center' }} className="text-muted">
                      {tab.id === 'all' && !q
                        ? 'No calls in this period.'
                        : `No calls match this filter${q ? ` (search “${q}”)` : ` — “${tab.label}”`}.`}
                    </td>
                  </tr>
                )}
                {loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: '28px 12px', textAlign: 'center' }} className="text-muted">Loading calls…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex-between" style={{ padding: 12, background: 'var(--bg-glass)' }}>
            <span className="mono text-xs text-muted">
              {rangeLabel({ page, size: PAGE_SIZE, total, shown: rows.length })}
              {counts ? ` · ${counts.all} call${counts.all === 1 ? '' : 's'} in this period` : ''}
            </span>
            <div className="flex-row">
              <span className="mono text-xs text-muted" style={{ marginRight: 8 }}>
                Page {page + 1} of {pageCount(total, PAGE_SIZE)}
              </span>
              <button className="btn small" disabled={!canGoPrev(page) || loading} onClick={() => goPage(page - 1)}>Prev</button>
              <button className="btn small" disabled={!canGoNext(page, total, PAGE_SIZE) || loading} onClick={() => goPage(page + 1)}>Next</button>
            </div>
          </div>
        </div>
      </div>

      {dialog && (dialog.action === 'COACH' ? (
        <CoachDialog
          title={`Assign ${selectedCount} call${selectedCount === 1 ? '' : 's'} to a coach`}
          options={userOptions}
          loading={usersLoading}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(v) => runBulk('COACH', v)}
        />
      ) : (
        <NoteDialog
          title={dialog.label}
          intro={`This applies to ${selectedCount} selected call${selectedCount === 1 ? '' : 's'}.`}
          confirmLabel="Confirm"
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(note) => runBulk(dialog.action, { note })}
        />
      ))}
    </>
  );
}
